import { KeyboardEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import CustomSelect from 'components/custom-select';
import SeletorDeCiclo, { CicloDoSeletor } from 'components/ui/seletor-de-ciclo';
import { FOCO_VISIVEL } from 'utils/foco';

export interface OpcaoDoSeletor {
  value: string;
  label: string;
}

/** Aviso de lateralidade (RDN-47): a página decide quando mostrar e o "Sair" faz o mesmo que o SAIR antigo. */
export interface LateralidadeDaFila {
  /** "validando" ou "apreciando", pela mesma regra do aviso antigo. */
  verbo: string;
  nome: string;
  detalhe: string;
  onSairKeyDown: (evento: KeyboardEvent<HTMLAnchorElement>) => void;
}

/** "Sublistas" do design = seletor de par da dupla MAD (mesmo estado e handler da página). */
export interface SublistasDaFila {
  opcoes: OpcaoDoSeletor[];
  valor: string | undefined;
  onEscolher: (valor: string) => void;
}

export interface SeletoresDaFilaProps {
  ciclos: CicloDoSeletor[];
  /** `_id` do ciclo em exibição. */
  ciclo: string;
  onCiclo: (cicloId: string) => void;
  fases: { _id: string; title: string }[];
  fase: string | undefined;
  onFase: (id: string) => void;
  desabilitado?: boolean;
  lateralidade?: LateralidadeDaFila;
  /** Abas de fluxo (`FlowTabs`) quando a página as mostraria. */
  listas?: ReactNode;
  sublistas?: SublistasDaFila;
}

/*
 * SPEC_DEVIATION: o segmentado "Fila de apreciação" do design veste as abas de fluxo (`FlowTabs`) por fora: a suíte
 * delas fixa as classes antigas (`bg-slate-blue`, `w-[45%]`, `rounded*`), então o visual do design entra como
 * sobreposição nos descendentes. A ativa fica `brand-blue-dark` com texto amarelo (contraste AA em texto de 12px).
 * Quando o `FlowTabs` não desenha nada (uma aba só não é escolha), o bloco inteiro some, sem faixa vazia.
 */
const SEGMENTADO_DAS_LISTAS = [
  '[&:not(:has(button))]:hidden',
  '[&>div]:inline-flex [&>div]:!justify-start [&>div]:gap-1 [&>div]:rounded-jj-md [&>div]:bg-surface-100 [&>div]:p-1',
  '[&_button]:!h-auto [&_button]:!w-auto [&_button]:!rounded-jj-sm [&_button]:!ring-0 [&_button]:cursor-pointer',
  '[&_button]:whitespace-nowrap [&_button]:px-4 [&_button]:py-[9px] [&_button]:font-display [&_button]:!text-xs [&_button]:!font-bold [&_button]:!normal-case',
  '[&_button]:!bg-transparent [&_button]:!text-content',
  '[&_button[aria-pressed=true]]:!bg-brand-blue-dark [&_button[aria-pressed=true]]:!text-brand-yellow',
  `[&_button]:focus-visible:outline [&_button]:focus-visible:outline-2 [&_button]:focus-visible:outline-offset-2 [&_button]:focus-visible:outline-brand-blue-dark`,
].join(' ');

const ROTULO = 'mb-[7px] block text-[11px] font-bold uppercase tracking-[0.05em] text-content-soft';

function Sublistas({ opcoes, valor, onEscolher }: SublistasDaFila) {
  const selecionado = valor ?? opcoes[0]?.value;

  return (
    <div>
      <span id="sublistas-da-fila" className="mb-[9px] block text-[11px] font-bold uppercase tracking-[0.05em] text-brand-navy">
        Sublistas · Ver realizações de
      </span>
      <div role="group" aria-labelledby="sublistas-da-fila" className="flex flex-wrap gap-2">
        {opcoes.map(opcao => (
          <button
            key={opcao.value}
            type="button"
            aria-pressed={opcao.value === selecionado}
            onClick={() => onEscolher(opcao.value)}
            className={`cursor-pointer rounded-jj-pill border px-[13px] py-2 text-[11.5px] font-semibold text-brand-navy ${
              opcao.value === selecionado ? 'border-brand-blue-dark bg-brand-white' : 'border-brand-navy bg-transparent'
            } ${FOCO_VISIVEL}`}
          >
            {opcao.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Ciclo, "Mês / fase", aviso de lateralidade e listas da fila do gestor (RDN-46, RDN-47). */
export default function SeletoresDaFila(props: SeletoresDaFilaProps) {
  const { lateralidade, listas, sublistas } = props;
  const opcoesDeFase = props.fases.map(fase => ({ value: fase._id, label: fase.title }));

  return (
    <section
      aria-label="Seleção da fila"
      className="mt-3.5 rounded-jj-panel bg-brand-white px-[22px] pb-4 pt-[18px] shadow-jj-01"
    >
      <div className="flex flex-wrap items-end gap-[18px]">
        <div className="min-w-[180px] flex-[1_1_100%] sm:flex-[0_1_200px]">
          <label htmlFor="fila-ciclo" className={ROTULO}>
            Ciclo
          </label>
          <SeletorDeCiclo
            ciclos={props.ciclos}
            valor={props.ciclo}
            onChange={props.onCiclo}
            rotulo="Ciclo"
            inputId="fila-ciclo"
            desabilitado={props.desabilitado}
          />
        </div>

        <div className="min-w-[250px] flex-[1_1_260px]">
          <label htmlFor="fila-fase" className={ROTULO}>
            Mês / fase
          </label>
          <CustomSelect
            inputId="fila-fase"
            ariaLabel="Mês / fase"
            variant="light"
            options={opcoesDeFase}
            value={opcoesDeFase.find(opcao => opcao.value === props.fase)}
            disabled={props.desabilitado}
            onChange={(opcao: any) => opcao?.value && props.onFase(opcao.value)}
          />
        </div>

        {listas && (
          <div role="group" aria-labelledby="listas-da-fila" className={`ml-auto max-w-full ${SEGMENTADO_DAS_LISTAS}`}>
            <span id="listas-da-fila" className={ROTULO}>
              Fila de apreciação
            </span>
            {listas}
          </div>
        )}
      </div>

      {lateralidade && (
        <div className="mt-4 flex flex-wrap items-start gap-3 rounded-jj-lg bg-brand-yellow-light px-[18px] py-3.5">
          <div className="min-w-0 flex-[1_1_320px]">
            <p className="m-0 font-display text-[13px] font-bold text-brand-blue-dark">
              Você está {lateralidade.verbo} no lugar de outro gestor
            </p>
            <p className="m-0 mt-1.5 text-[11.5px] leading-[1.6] text-content">
              <b>{lateralidade.nome}</b> · {lateralidade.detalhe}
            </p>
          </div>
          <Link
            to="/"
            onKeyDown={lateralidade.onSairKeyDown}
            className={`self-center rounded-jj-sm border border-brand-blue-dark bg-brand-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.04em] text-brand-blue-dark no-underline hover:text-brand-blue-dark hover:no-underline ${FOCO_VISIVEL}`}
          >
            Sair
          </Link>
        </div>
      )}

      {sublistas && (
        <div className="mt-4 rounded-jj-lg bg-brand-green px-[18px] py-4">
          <Sublistas {...sublistas} />
        </div>
      )}
    </section>
  );
}
